import { useState } from "react";
import { Wand2, Upload, Save, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { targetOptions, langDisplayName } from "@/lib/languages";
import AssetPlayer from "@/components/assets/AssetPlayer";

export const OUTPUTS = ["worksheet", "grammar_discovery", "flashcards", "crossword", "interactive_form", "reading", "speaking_cards", "exit_ticket", "data_challenge"];
const UPLOAD_ACCEPT = ".pdf,.docx,.txt,.csv,.xlsx";
const EMPTY = { output_type: "worksheet", cefr: "A2", language: "fr", theme: "", grammar_focus: "", vocab_focus: "", function: "", duration: 15, grouping: "individual", instructions: "", canonical: false, eligibility: ["education"] };

function SpecForm({ s, set, admin }) {
  const { t, locale } = useI18n();
  const inp = (k, ph, max = 120) => <input className="nb-input" dir="auto" maxLength={max} placeholder={ph} value={s[k]} onChange={(e) => set(k, e.target.value)} data-testid={`ds-${k}`} />;
  return (
    <div className="grid sm:grid-cols-3 gap-2">
      <select className="nb-input" value={s.output_type} onChange={(e) => set("output_type", e.target.value)} data-testid="ds-output">{OUTPUTS.map((o) => <option key={o} value={o}>{t(`al.p.out.${o}`)}</option>)}</select>
      <select className="nb-input" value={s.language} onChange={(e) => set("language", e.target.value)} data-testid="ds-language">{targetOptions().map((l) => <option key={l.code} value={l.code}>{langDisplayName(l.code, locale)}</option>)}</select>
      <select className="nb-input" value={s.cefr} onChange={(e) => set("cefr", e.target.value)} data-testid="ds-cefr">{["A1", "A2", "B1", "B2", "C1", "C2"].map((c) => <option key={c}>{c}</option>)}</select>
      {inp("theme", t("al.p.theme"), 80)}{inp("grammar_focus", t("al.p.grammar"))}{inp("vocab_focus", t("al.p.vocab"))}
      {inp("function", t("al.p.function"), 80)}
      <input type="number" min={3} max={90} className="nb-input" value={s.duration} onChange={(e) => set("duration", Number(e.target.value))} aria-label={t("al.p.duration")} data-testid="ds-duration" />
      <select className="nb-input" value={s.grouping} onChange={(e) => set("grouping", e.target.value)} data-testid="ds-grouping">{["individual", "pairs", "groups", "whole_class"].map((g) => <option key={g} value={g}>{t(`al.p.grp.${g}`)}</option>)}</select>
      <textarea className="nb-input sm:col-span-3" rows={2} dir="auto" maxLength={800} placeholder={t("al.p.teacherInstr")} value={s.instructions} onChange={(e) => set("instructions", e.target.value)} data-testid="ds-instructions" />
      {admin && (
        <label className="text-sm flex items-center gap-2 sm:col-span-3"><input type="checkbox" checked={s.canonical} onChange={(e) => set("canonical", e.target.checked)} data-testid="ds-canonical" /> {t("al.p.canonical")}</label>
      )}
    </div>
  );
}

export function DraftPreview({ draft, onSaved }) {
  const { t } = useI18n();
  const a = draft.asset, lang = a.target_languages[0], r = a.renders[lang];
  const [title, setTitle] = useState(a.title);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const x = (await api.post(`/studio/drafts/${draft.preview_id}/save`, { title })).data; toast.success(t("al.p.draftSaved")); onSaved(x); }
    catch (e) { toast.error(e?.response?.data?.detail || t("al.failed")); } finally { setBusy(false); }
  };
  return (
    <div className="nb-card bg-white p-4 space-y-3 mt-4" data-testid="ds-preview">
      <div className="text-[11px] font-bold uppercase tracking-widest rtl:tracking-normal text-coral">{t("al.p.draftNote")}</div>
      <input className="nb-input font-bold" dir="auto" maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} aria-label={t("al.titleLabel")} data-testid="ds-preview-title" />
      <p className="text-sm text-gray-600" dir="auto">{a.student_instructions}</p>
      {a.provenance?.source_filename && <p className="text-xs flex items-center gap-1" data-testid="ds-source"><FileText className="w-3 h-3" /> {t("al.p.fromFile")} <span dir="auto">{a.provenance.source_filename}</span></p>}
      <AssetPlayer family={a.family} language={lang} content={r} answers={r.answers} />
      {a.teacher?.instructions && <div className="nb-card bg-butter p-3 text-sm" data-testid="ds-teacher"><b>{t("al.notes")}:</b> <span dir="auto">{a.teacher.instructions}</span></div>}
      <button onClick={save} disabled={busy} className="nb-btn-primary py-2 px-4" data-testid="ds-save"><Save className="w-4 h-4" /> {t("al.p.saveDraft")}</button>
    </div>
  );
}

export default function DraftStudio({ onSaved }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [s, setS] = useState(EMPTY);
  const [mode, setMode] = useState("ai");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const run = async (fn) => { setBusy(true); setDraft(null); try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || t("al.failed")); } finally { setBusy(false); } };
  const generate = () => run(async () => setDraft((await api.post("/studio/drafts/generate", s)).data));
  const upload = (f) => run(async () => { const fd = new FormData(); fd.append("file", f); setFile((await api.post("/library/resources", fd)).data); toast.success(t("al.p.uploaded")); });
  const fromFile = () => run(async () => setDraft((await api.post(`/resources/uploads/${file.resource_id}/to-activity`, { ...s, canonical: false })).data));
  return (
    <div className="mt-5 space-y-3" data-testid="draft-studio">
      <div className="flex gap-1">{["ai", "upload"].map((m) => <button key={m} onClick={() => { setMode(m); setDraft(null); }} className={`nb-tag text-sm ${mode === m ? "bg-ink text-white" : "bg-white"}`} data-testid={`ds-mode-${m}`}>{t(`al.p.mode.${m}`)}</button>)}</div>
      <p className="text-xs text-gray-600">{t(mode === "ai" ? "al.p.aiNote" : "al.p.uploadNote")}</p>
      {mode === "upload" && (
        <div className="nb-card p-3 space-y-2" data-testid="ds-upload">
          <input type="file" className="w-full max-w-full text-sm" accept={UPLOAD_ACCEPT} onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="ds-file" />
          {file && (
            <div className="text-sm space-y-1" data-testid="ds-file-choice">
              <div><b dir="auto">{file.filename}</b> — {t("al.p.uploadedAs")}</div>
              <div className="flex gap-2 flex-wrap"><span className="nb-tag bg-mint text-xs" data-testid="ds-use-resource">{t("al.p.useAsResource")}</span><span className="text-xs text-gray-600">{t("al.p.orCreate")}</span></div>
            </div>
          )}
        </div>
      )}
      <SpecForm s={s} set={set} admin={user?.is_admin && mode === "ai"} />
      <button onClick={mode === "ai" ? generate : fromFile} disabled={busy || (mode === "upload" && !file)} className="nb-btn-primary py-2 px-4 disabled:opacity-50" data-testid="ds-generate">
        {mode === "ai" ? <Wand2 className="w-4 h-4" /> : <Upload className="w-4 h-4" />} {busy ? t("al.p.generating") : t(mode === "ai" ? "al.p.generate" : "al.p.createFromFile")}
      </button>
      {draft && <DraftPreview key={draft.preview_id} draft={draft} onSaved={(x) => { setDraft(null); onSaved?.(x); }} />}
    </div>
  );
}
